/**
 * Accepted payment methods (content sheet): Visa, Mastercard, mada, Apple Pay,
 * Paymob, tabby, tamara, madfu. Drawn as light-weight wordmark badges (no
 * external images) so they stay crisp and load instantly.
 */

type Method = { key: string; label: string; node: React.ReactNode };

const METHODS: Method[] = [
  {
    key: "visa",
    label: "Visa",
    node: <span className="font-extrabold italic tracking-tight text-[#1a1f71]">VISA</span>,
  },
  {
    key: "mastercard",
    label: "Mastercard",
    node: (
      <svg viewBox="0 0 32 20" className="h-4 w-auto" aria-hidden>
        <circle cx="12" cy="10" r="8" fill="#eb001b" />
        <circle cx="20" cy="10" r="8" fill="#f79e1b" />
        <path d="M16 3.8a8 8 0 0 1 0 12.4 8 8 0 0 1 0-12.4Z" fill="#ff5f00" />
      </svg>
    ),
  },
  {
    key: "mada",
    label: "mada",
    node: (
      <span className="font-extrabold tracking-tight">
        <span className="text-[#259bd6]">ma</span>
        <span className="text-[#84b740]">da</span>
      </span>
    ),
  },
  {
    key: "applepay",
    label: "Apple Pay",
    node: (
      <span className="flex items-center gap-0.5 font-semibold text-black">
        <svg viewBox="0 0 14 17" className="h-3.5 w-auto" aria-hidden>
          <path
            fill="currentColor"
            d="M11.6 9c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3-1.6-1.3-.1-2.5.8-3.1.8-.7 0-1.6-.8-2.7-.7-1.4 0-2.7.8-3.4 2.1-1.5 2.5-.4 6.3 1 8.4.7 1 1.5 2.1 2.6 2.1 1-.1 1.4-.7 2.7-.7 1.2 0 1.6.7 2.7.6 1.1 0 1.8-1 2.5-2 .8-1.1 1.1-2.2 1.1-2.3-.1 0-2.2-.8-2.2-3.5ZM9.5 2.8c.6-.7 1-1.7.9-2.7-.9 0-1.9.6-2.5 1.3-.5.6-1 1.6-.9 2.6 1 .1 1.9-.5 2.5-1.2Z"
          />
        </svg>
        Pay
      </span>
    ),
  },
  { key: "paymob", label: "Paymob", node: <span className="font-extrabold tracking-tight text-[#0b5bd3]">paymob</span> },
  {
    key: "tabby",
    label: "tabby",
    node: <span className="rounded-[4px] bg-[#3effc1] px-1 font-extrabold tracking-tight text-black">tabby</span>,
  },
  {
    key: "tamara",
    label: "tamara",
    node: (
      <span className="bg-gradient-to-r from-[#f7a07a] via-[#e18dc9] to-[#9f8bf5] bg-clip-text font-extrabold tracking-tight text-transparent">
        tamara
      </span>
    ),
  },
  { key: "madfu", label: "madfu", node: <span className="font-extrabold tracking-tight text-[#5a2d82]">madfu</span> },
];

export default function PaymentMethods({ className = "", size = "md" }: { className?: string; size?: "sm" | "md" }) {
  const box = size === "sm" ? "h-7 px-2 text-[11px]" : "h-8 px-2.5 text-xs";
  return (
    <ul className={`flex flex-wrap items-center gap-1.5 ${className}`} aria-label="Payment methods" dir="ltr">
      {METHODS.map((m) => (
        <li
          key={m.key}
          title={m.label}
          aria-label={m.label}
          className={`flex items-center justify-center rounded-md border border-black/5 bg-white shadow-sm ${box}`}
        >
          {m.node}
        </li>
      ))}
    </ul>
  );
}
